import React, { useState, useEffect } from 'react';
import { BottomAppShowcase, PageShell } from './public/PublicPrimitives';
import { CleroS, CleroWordmarkSvg } from './Logo';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  Check,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Lock,
  ArrowRight,
} from 'lucide-react';
import Lenis from 'lenis';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../constants/downloadLinks';

export { ANDROID_INVITE_URL, IOS_INVITE_URL };

const DataDeletionPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.0,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateEmail = (val: string) => {
    return /\S+@\S+\.\S+/.test(val);
  };

  const formatPhoneDisplay = (num: string) => {
    const cleaned = num.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(raw);
    if (errorMessage) setErrorMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    let hasError = false;
    if (!email.trim() || !validateEmail(email)) {
      setErrorMessage('Please enter a valid contact email address.');
      hasError = true;
    } else if (phone.replace(/\D/g, '').length < 10) {
      setErrorMessage('Please enter a valid 10-digit phone number associated with your account.');
      hasError = true;
    }

    if (hasError) return;

    setSubmitting(true);
    const generatedTicketId = `CLR-SUP-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

    try {
      const submitFeedback = httpsCallable(functions, 'submitUserFeedback');

      const messageContent = reason.trim()
        ? `User requested account & data deletion.\nReason / Details: ${reason.trim()}`
        : 'User requested complete account and data deletion via https://clero.money/data-deletion.';

      const res: any = await submitFeedback({
        type: 'support',
        topic: 'Account & Data Deletion',
        message: messageContent,
        senderEmail: email.trim(),
        senderPhone: `+1${phone.replace(/\D/g, '')}`,
        ticketId: generatedTicketId,
      });

      const confirmedTicketId = res?.data?.ticketId || generatedTicketId;
      setSubmittedTicketId(confirmedTicketId);
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err?.message || 'Failed to submit request. Please try again or email privacy@clero.money.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell
      background="bg-[#fafaf7]"
      description="Permanently delete your Clero account, transaction metadata, and connected bank tokens."
      title="Data Deletion | Clero"
    >
      {/* FLOATING HEADER */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled ? 'pt-3.5 px-4 sm:px-6' : 'pt-0 px-0'
        }`}
      >
        <header
          className={`pointer-events-auto w-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? 'max-w-[480px] rounded-[999px] bg-white/85 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm'
              : 'max-w-[720px] sm:max-w-[760px] rounded-[0px] bg-transparent backdrop-blur-none border-b border-slate-200/50 px-4 sm:px-6 py-3.5'
          }`}
        >
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-2 shrink-0">
              <a
                aria-label="Clero Home"
                className="flex items-center transition opacity-95 hover:opacity-100"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
              >
                <CleroWordmarkSvg height={24} color="#0D9488" />
              </a>
            </div>

            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
              <button
                className="text-xs sm:text-sm font-bold text-teal-800 hover:text-teal-950 transition-colors whitespace-nowrap px-1 py-1"
                onClick={() => navigate('/support')}
                type="button"
              >
                Help & Support
              </button>

              <button
                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
                onClick={() => {
                  const el = document.getElementById('bottom-download');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                type="button"
              >
                Download App
              </button>
            </div>
          </div>
        </header>
      </div>

      <div className="h-16 sm:h-20" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-10 sm:pb-14 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-3.5">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-950">
            Request Account & Data Deletion
          </h1>
          <p className="mx-auto max-w-xl text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
            We're here to help. Submit your request below and our team will get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* MAIN FORM MODULE */}
      <section className="py-4 sm:py-6 px-4 sm:px-6">
        <div className="mx-auto max-w-2xl space-y-8">
          <div className="rounded-3xl sm:rounded-[32px] bg-white p-6 sm:p-10 lg:p-12 border border-slate-200/80 shadow-sm">
            {submittedTicketId ? (
              <div className="text-center py-8 sm:py-10 space-y-5 max-w-md mx-auto animate-in fade-in zoom-in-95 duration-300">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <div className="space-y-2">
                  <span className="text-xs font-extrabold uppercase tracking-widest text-teal-700 block">
                    Deletion Request Submitted
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                    We’re on it!
                  </h3>
                  <p className="text-sm font-medium text-slate-600 leading-relaxed">
                    Your request has been logged under ticket reference:
                  </p>
                  <div className="inline-block rounded-2xl bg-slate-100 px-4 py-2 text-xs font-mono font-bold text-slate-900 border border-slate-200">
                    {submittedTicketId}
                  </div>
                  <p className="text-xs font-medium text-slate-500 leading-relaxed pt-2">
                    A representative from the <strong className="text-slate-800">Clero team</strong> will review your details and respond via email within 24 hours.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSubmittedTicketId(null);
                    setReason('');
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-6 py-3 text-xs font-bold text-white hover:bg-slate-800 transition-all cursor-pointer"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Submit Another Request</span>
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-slate-950 tracking-tight">
                    Submit Deletion Request
                  </h3>
                  <p className="text-xs sm:text-sm font-medium text-slate-500 mt-1">
                    Please provide your contact information and details below.
                  </p>
                </div>

                {/* What Gets Deleted List */}
                <div className="rounded-2xl bg-slate-50/80 border border-slate-200/60 p-4 sm:p-5 space-y-3">
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-500">
                    What gets permanently deleted:
                  </h4>
                  <ul className="space-y-2.5 text-xs sm:text-sm font-semibold text-slate-700">
                    <li className="flex items-center gap-2.5">
                      <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-md bg-teal-100 text-teal-800">
                        <Check className="h-2.5 w-2.5 stroke-[3]" />
                      </div>
                      <span>Account & Profile: Your name, username, profile details, and login</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-md bg-teal-100 text-teal-800">
                        <Check className="h-2.5 w-2.5 stroke-[3]" />
                      </div>
                      <span>Connected Banks & Cards: Unlinks all bank accounts and credit cards</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-md bg-teal-100 text-teal-800">
                        <Check className="h-2.5 w-2.5 stroke-[3]" />
                      </div>
                      <span>Payment History & Receipts: Group splits, transaction history, and scanned receipts</span>
                    </li>
                  </ul>
                </div>

                {errorMessage && (
                  <div className="rounded-2xl bg-red-50 border border-red-200/80 p-4 text-xs sm:text-sm font-semibold text-red-700">
                    {errorMessage}
                  </div>
                )}

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="deletion-email"
                      className="block text-xs font-bold uppercase tracking-wider text-slate-700"
                    >
                      Email <span className="text-teal-700">*</span>
                    </label>
                    <input
                      id="deletion-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errorMessage) setErrorMessage(null);
                      }}
                      placeholder="you@example.com"
                      className="w-full rounded-2xl bg-slate-50/70 border border-slate-200/80 px-4 py-3 text-sm font-semibold text-slate-950 placeholder:text-slate-400 focus:bg-white focus:border-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-700/10 transition-all"
                    />
                    <p className="text-[11px] text-slate-400 font-medium">
                      We'll use this email to respond to your request.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor="deletion-phone"
                      className="block text-xs font-bold uppercase tracking-wider text-slate-700"
                    >
                      Account Phone <span className="text-teal-700">*</span>
                    </label>
                    <input
                      id="deletion-phone"
                      type="tel"
                      value={formatPhoneDisplay(phone)}
                      onChange={handlePhoneChange}
                      placeholder="(555) 000-0000"
                      className="w-full rounded-2xl bg-slate-50/70 border border-slate-200/80 px-4 py-3 text-sm font-semibold text-slate-950 placeholder:text-slate-400 focus:bg-white focus:border-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-700/10 transition-all"
                    />
                    <p className="text-[11px] text-slate-400 font-medium">
                      The phone number linked to your Clero account.
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="deletion-reason"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-700"
                  >
                    Reason or Notes <span className="text-slate-400 font-normal">(Optional)</span>
                  </label>
                  <textarea
                    id="deletion-reason"
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Tell us why you're leaving or any additional details..."
                    className="w-full rounded-2xl bg-slate-50/70 border border-slate-200/80 p-4 text-sm font-semibold text-slate-950 placeholder:text-slate-400 focus:bg-white focus:border-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-700/10 transition-all resize-none"
                  />
                </div>

                {/* Important Notes */}
                <div className="space-y-2">
                  <div className="rounded-2xl bg-amber-50/70 border border-amber-200/60 p-3.5 text-xs text-amber-900 font-medium leading-relaxed">
                    <strong>Important:</strong> Your account will only be deleted once any remaining balances and pending money transfers have fully cleared. A Clero team member will reach out to keep you informed if there are active transfers or balances on your account.
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-3.5 text-xs text-slate-500 font-medium leading-relaxed">
                    Note: Financial settlement records required for regulatory auditing compliance are handled per banking industry retention standards by licensed payment partners.
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 order-2 sm:order-1">
                    <Lock className="h-3.5 w-3.5 text-slate-400" />
                    <span>Confidential & Encrypted Request</span>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-teal-800 hover:bg-teal-900 px-8 py-3.5 text-sm font-bold text-white shadow-sm transition-all hover:shadow active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer order-1 sm:order-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit request</span>
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Privacy Team Direct Link */}
          <div className="text-center text-xs text-slate-500 font-medium">
            Need manual assistance? Email our privacy officer directly at{' '}
            <a href="mailto:privacy@clero.money" className="font-bold text-teal-700 hover:underline">
              privacy@clero.money
            </a>
          </div>
        </div>
      </section>

      <div className="mt-12 mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8 mb-12">
        <BottomAppShowcase />
      </div>

      {/* FOOTER */}
      <footer className="border-t border-slate-200/80 bg-white px-4 py-12 sm:px-6 lg:px-8 mt-16">
        <div className="mx-auto max-w-[var(--clero-max-content)]">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <img src="/clero_icon.png" alt="Clero" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl shadow-sm object-cover" />
              <p className="text-xs font-semibold text-slate-400">
                Secured with direct bank connections via Plaid & Astra Financial.
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Compare</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare')} type="button">
                    All Splitting Apps
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/splitwise')} type="button">
                    Clero vs Splitwise
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/venmo')} type="button">
                    Clero vs Venmo
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/zelle')} type="button">
                    Clero vs Zelle
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/')} type="button">Features</button></li>
                <li><a className="transition hover:text-slate-950" href={IOS_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for iOS</a></li>
                <li><a className="transition hover:text-slate-950" href={ANDROID_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for Android</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/blog')} type="button">Blog</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/support')} type="button">Help & Support</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/compare')} type="button">Compare Apps</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Legal & Privacy</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/privacy')} type="button">Privacy Policy</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/terms')} type="button">Terms of Service</button></li>
                <li><button className="text-teal-700 font-bold" onClick={() => navigate('/data-deletion')} type="button">Data Deletion</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 text-center text-xs text-slate-400">
            <p>© 2026 Oryn Technologies Inc. All rights reserved. Clero is a financial technology platform, not a bank.</p>
          </div>
        </div>
      </footer>
    </PageShell>
  );
};

export default DataDeletionPage;

